<template>
  <body v-if="resource">
    <!-- Panoramic Component -->
    <div class="pano-wrapper">
      <h-pano
        v-if="template"
        class="hero-image pano-child"
        :source="cloud(template.cover)"
        :controlButtons="['fullscreen']"
        :disableZoom="true"
      />
      <div class="pano-overlay pano-child" />
    </div>

    <!-- Metadata information -->
    <section class="has-text-left body-container">
      <div class="grid-container">
        <div class="placements-table-wrapper">
          <!-- Title and Pill -->
          <div class="title-and-pill">
            <!-- Title -->
            <h3 class="title">
              <h-content-editable
                tag="span"
                :contenteditable="true"
                v-model="resource.title"
                :noNL="true"
                :noHTML="false"
                placeholder="Untitled"
              />
            </h3>

            <!-- Pill -->
            <span class="pill" @click="resource.public = !resource.public">
              {{ publicPrivateText }}
            </span>
          </div>

          <!-- Description -->
          <h-content-editable
            tag="div"
            :contenteditable="true"
            v-model="resource.description"
            :noNL="false"
            :noHTML="false"
            placeholder="Give your space a description..."
          />

          <!-- Taglist -->
          <div class="taglist">
            <h-split-tag
              type="is-light"
              textLeft="Template"
              :textRight="(template && template.title) || 'Select'"
              @click.native="launchTemplatesSelect"
            >
            </h-split-tag>

            <div class="tags license-dropdown">
              <b-dropdown v-model="resource.license[0]">
                <template slot="trigger">
                  <h-split-tag
                    type="is-light"
                    textLeft="License"
                    :textRight="
                      (resource.license && resource.license[0]) || 'Select'
                    "
                  >
                  </h-split-tag>
                </template>

                <b-dropdown-item
                  v-for="license in licenses"
                  :key="license.value"
                  :value="license.value"
                >
                  <div class="media">
                    <div class="media-content">
                      <div>{{ license.value }}</div>
                      <small>{{ license.description }}</small>
                    </div>
                  </div>
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <!-- Existing Tags -->
            <b-tag v-for="(tag, index) in resource.tags" :key="tag" ellipsis>
              <div class="center-align-icon">
                {{ tag }}
                <b-icon
                  style="margin-right: 1px; cursor: pointer"
                  icon="X"
                  size="is-small"
                  @click.native="handleTagRemove(index)"
                ></b-icon>
              </div>
            </b-tag>

            <!-- New, Editable tag -->
            <b-tag ellipsis @click.native="toggleNewTag"
              ><h-content-editable
                tag="div"
                :contenteditable="true"
                v-model="newTag"
                :noNL="true"
                :noHTML="true"
                placeholder="Add New Tag..."
                @keydown="handleNewTagKeydown"
              />
            </b-tag>
          </div>

          <div
            class="placements-table"
            id="h-placements"
            v-if="allPlacements.length"
          >
            <b-table
              :data="allPlacements"
              :debounce-search="1000"
              :per-page="15"
              :default-sort="`transactionDate`"
              v-slot="props"
              paginated
            >
              <b-table-column
                field="title"
                label="Title"
                sortable
                :class="props.row.inFlight && `inFlightOpacity`"
              >
                <a @click="modal(props.row)">{{
                  props.row.title | truncate(25)
                }}</a>
              </b-table-column>
              <b-table-column
                field="itemIndex"
                label="Type"
                sortable
                :class="props.row.inFlight && `inFlightOpacity`"
              >
                <b-tooltip
                  :label="singularCapitalized(props.row.itemIndex)"
                  position="is-left"
                  type="is-primary"
                >
                  <div class="icon-parent">
                    <div class="icon-background"></div>
                    <img
                      class="badge"
                      :src="
                        `/img/icons/doctype-badges/${props.row.itemIndex}.svg`
                      "
                      :alt="`${props.row.itemIndex} badge`"
                    />
                  </div>
                </b-tooltip>
              </b-table-column>
              <b-table-column
                field="transactionDate"
                label="Date Added"
                sortable
                :class="props.row.inFlight && `inFlightOpacity`"
              >
                {{ props.row.transactionDate | long_date }}
              </b-table-column>
              <b-table-column :class="props.row.inFlight && `inFlightOpacity`">
                <b-dropdown
                  position="is-bottom-left"
                  :close-on-click="false"
                  v-if="!props.row.inFlight"
                >
                  <b-icon slot="trigger" icon="more-vertical"> </b-icon>
                  <b-dropdown-item
                    aria-role="listitem"
                    :disabled="deletingPlacement"
                  >
                    <h-delete-dropdown-item
                      :deleting="deletingPlacement"
                      @delete="deletePlacement(props.row)"
                    />
                  </b-dropdown-item>
                </b-dropdown>
                <b-icon
                  v-else
                  icon="loader"
                  size="is-small"
                  class="spin"
                ></b-icon>
              </b-table-column>
            </b-table>
          </div>

          <!-- Subitems Dropzone -->
          <b-field>
            <b-upload v-model="dropFiles" multiple drag-drop :expanded="true">
              <section class="section">
                <div class="content has-text-centered">
                  <p>
                    <b-icon icon="upload" size="is-large"> </b-icon>
                  </p>
                  <p>Drop your files here or click to upload</p>
                </div>
              </section>
            </b-upload>
          </b-field>
        </div>
        <div class="orb-and-grid">
          <div class="orb-wrapper">
            <div class="orb-and-shadow">
              <div class="orb">
                <model-viewer
                  v-if="template"
                  @load="initOrbRotation"
                  id="modelViewer"
                  :src="aws('spaces_resources/reflective-sphere.gltf')"
                  :environment-image="cloud(template.cover)"
                  data-js-focus-visible
                  camera-controls
                  disable-zoom
                  interaction-prompt="none"
                  shadow-intensity="0"
                  max-camera-orbit="auto 90deg auto"
                  min-camera-orbit="auto 90deg auto"
                >
                  <div class="progress-bar hide" slot="progress-bar">
                    <div class="update-bar"></div>
                  </div>
                </model-viewer>
              </div>
              <div class="shadow-wrapper">
                <div class="shadow"></div>
              </div>
            </div>
          </div>
          <TransitionGroup class="grid" tag="div" name="list">
            <div
              v-for="(placement, i) in placementCards"
              :key="placement.id"
              :class="`item-${i}`"
              @click="modal(placement)"
            >
              <component
                v-if="placement"
                :is="`h-${placement.itemIndex}-card`"
                :disableLink="true"
                :tilt="true"
                :shadow="true"
                :content="placement"
                :width="`100%`"
                :index="i"
                :tiltBounds="[5, 10]"
              />
            </div>
          </TransitionGroup>
        </div>
      </div>
    </section>
  </body>
</template>
<script>
import { mapGetters } from "vuex";
import { clone, debounce, capitalize } from "lodash";
import { showToast } from "@/common/toast.service.js";
import { showModal } from "@/common/modal.service.js";
import { resourceModules } from "@/common/resources/index.js";
import { modalMap } from "@/components/base/resourceModals.js";
import * as cards from "@/components/base/resourceCards.js";
import * as ModalElements from "@/components/spaces/index.js";
import TemplatesSelect from "@/components/t_spaces/TemplatesSelect.modal.vue";
import HPano from "@/components/spaces/HPano.vue";
import "@google/model-viewer";
import Templates from "@/common/resources/Templates.module.js";
import { CHECK_AUTH } from "@/store/actions.type";

const UPDATE_REQUEST_DEBOUNCE = 5000;

// Async function to read the contents of a .txt file
function readTextFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = function(e) {
      resolve(e.target.result);
    };

    reader.onerror = function(e) {
      reject(e);
    };
    reader.readAsText(file);
  });
}

// Mappings of file types to Holos resources
const typeMappings = {
  "video/mp4": "t_videos",
  "video/quicktime": "t_videos",
  "image/svg+xml": "t_pictures",
  "image/png": "t_pictures",
  "image/jpeg": "t_pictures",
  "text/rtf": "t_texts",
  "text/plain": "t_texts",
  "audio/mpeg": "t_audios",
  "model/glb": "t_models",
};

// Functions to generate unique fields to each resource
const specialFieldDefs = {
  t_audios: async file => {
    return {
      spatialized: false,
      src: file,
      provider: "User upload",
    };
  },
  t_pictures: async file => {
    return {
      src: file,
      provider: "User upload",
    };
  },
  t_videos: async file => {
    return {
      src: file,
      provider: "User upload",
    };
  },
  t_models: async file => {
    return {
      src: file,
      provider: "Own Creation",
    };
  },
  t_texts: async file => {
    // Parse text from text file and set it as the text field
    const text = await readTextFile(file);

    return {
      text,
      provider: "User upload",
    };
  },
};

export default {
  components: { ...cards, HPano, ...ModalElements.default },
  data() {
    return {
      resource: undefined,
      newTag: "",
      loading: false,
      template: undefined,
      templates: undefined,
      placements: [],
      dropFiles: [],
      uploading: false,
      deletingPlacement: false,
      placementsInFlight: [],
      licenses: [
        {
          value: "CC0",
          description:
            "Give up copyright and put your work into the public domain",
        },
        {
          value: "BY",
          description: "Credit must be given to you, the creator",
        },
        {
          value: "BY-SA",
          description:
            "Credit must be given to you, the creator. Adaptations must be shared under the same terms",
        },
        {
          value: "BY-NC",
          description:
            "Credit must be given to you, the creator. Only noncommercial uses of the work are permitted.",
        },
        {
          value: "BY-NC-SA",
          description:
            "Credit must be given to you, the creator. Only noncommercial uses of the work are permitted. Adaptations must be shared under the same terms.",
        },
        {
          value: "BY-ND",
          description:
            "Credit must be given to you, the creator. No derivatives or adaptations of the work are permitted.",
        },
        {
          value: "BY-NC-ND",
          description:
            "Credit must be given to you, the creator. Only noncommercial uses of the work are permitted. No derivatives or adaptations of the work are permitted.",
        },
      ],
    };
  },
  created() {
    this.setInfo();
    this.setPlacements();
  },
  methods: {
    async setPlacements() {
      this.placements = (await this.resource.$getPlacements(true)).data.map(
        p =>
          new resourceModules[p.itemIndex]({
            ...p,
            id: p.itemID,
            placementID: p.id,
          }),
      );
    },
    singularCapitalized(name) {
      return capitalize(name.slice(0, name.length - 1));
    },
    async deletePlacement(placement) {
      this.deletingPlacement = true;
      // Placements were cast as itemIndex types in this.setPlacements. Cast back to placements resource before removing
      await new resourceModules.placements({
        id: placement.placementID,
      }).$remove();

      this.deletingPlacement = false;

      await this.setPlacements();
    },
    async createAndPlace() {
      const getResourceType = file => {
        const modelException =
          !file.type && file.name.includes(".glb") && "model/glb";
        return typeMappings[file.type || modelException];
      };

      let copyDropFiles = [...this.dropFiles];

      this.dropFiles = [];
      this.uploading = true;

      // Create entries for inFlight placements
      const newInFlight = copyDropFiles.map(file => {
        return {
          title: file.name,
          transactionDate: new Date(),
          itemIndex: getResourceType(file),
          inFlight: true,
        };
      });
      this.placementsInFlight = [...newInFlight, ...this.placementsInFlight];

      // Upload all files
      for (let file of copyDropFiles) {
        // Get resource type from file type or name
        const resourceType = getResourceType(file);

        // Not all file types are supported
        if (!resourceType) {
          showToast("File type is not supported", "is-danger", 5000);
          this.uploading = false;
          return;
        }

        // Get fields unique to this resource
        const specialFields = await specialFieldDefs[resourceType](file);

        // Create new resource
        const Resource = resourceModules[resourceType];
        const resource = await new Resource({
          title: file.name,
          description: "",
          price: 0,
          currency: "usd",
          license: ["CC0"],
          public: false,
          ...specialFields,
        }).$create();

        // Place the new resource
        const Placements = resourceModules.placements;
        await new Placements({
          itemID: resource.data.id,
          itemIndex: resourceType,
          targetID: this.resource.id,
          targetIndex: "t_spaces",
        }).$create();
      }

      // Pull placements
      await this.setPlacements();

      // Clear out all in-flight placements
      this.placementsInFlight = [];

      //Dispatch CHECK_AUTH action to update currentUser in vuex store
      await this.$store.dispatch(CHECK_AUTH);

      this.uploading = false;
    },
    updateSpace: debounce(async function() {
      this.loading = true;
      try {
        // Lodash.omit won't work here since it removes $update()
        const copy = clone(this.resource);
        const toOmit = [
          "developerID",
          "createdAt",
          "placements",
          "rejections",
          "approvals",
        ];
        toOmit.forEach(key => {
          delete copy[key];
        });

        // Send update request
        await copy.$update();
        const message = "Your space was saved!";
        showToast(message, "is-success", 5000);
        this.loading = false;
      } catch (err) {
        this.loading = false;
        showToast(err.description, "is-danger", 5000);
      }
    }, UPDATE_REQUEST_DEBOUNCE),
    launchTemplatesSelect() {
      showModal({
        props: { templates: this.templates },
        hasModalCard: true,
        overflow: true,
        component: TemplatesSelect,
        parent: this,
        onCancel: e => console.log("Closed via button: ", e),
        events: {
          close: e => console.log("Closed via button: ", e),
          selected: e => {
            this.template = e;
          },
        },
      });
    },
    handleNewTagKeydown(e) {
      if (e.key === "Enter" && this.newTag.length > 0) {
        this.resource.tags.push(this.newTag);
        this.newTag = "";
      }
    },
    toggleNewTag() {
      if (!this.creatingNewTag) this.creatingNewTag = true;
    },
    handleTagRemove(index) {
      if (this.resource && this.resource.tags)
        this.resource.tags.splice(index, 1);
    },
    async modal({ itemIndex, itemID }) {
      const resource = (await resourceModules[itemIndex].byID(itemID, true))
        .data;
      const component = modalMap[itemIndex];

      resource.tags = resource.tags || [];
      showModal({
        props: { resource, privateType: itemIndex.startsWith("t_") },
        hasModalCard: true,
        overflow: true,
        component,
        parent: this,
        onCancel: () => this.setPlacements(),
        events: {
          close: () => this.setPlacements(),
        },
      });
    },
    async setInfo() {
      let copy = this.$route.meta.data;
      if (!copy.tags) copy.tags = [];
      if (!copy.public) copy.public = false;
      this.resource = copy;

      // Load all templates. Set template to basicBox if space doesn't already have a template
      this.templates = (await Templates.all()).data;
      const spaceTemplate = this.templates.find(
        t => t.id === this.resource.template,
      );
      this.template = spaceTemplate || this.templates[0];
    },
    initOrbRotation() {
      let orbInteraction = false;
      let timer;

      //Get model-viewer component
      const mv = document.getElementById("modelViewer");

      if (mv) {
        mv.addEventListener("mousedown", () => setRotation(true, 0));
        mv.addEventListener("mouseup", () => setRotation(false, 3000));
        mv.addEventListener("mouseleave", () => setRotation(false, 3000));
        mv.addEventListener("touchstart", () => setRotation(true, 0));
        mv.addEventListener("touchend", () => setRotation(false, 3000));
        mv.addEventListener("touchcancel", () => setRotation(false, 3000));

        //Start rotating the orb
        updateOrbit();
      }

      //Register event handlers for user interaction
      function setRotation(value, delay) {
        clearTimeout(timer);
        timer = setTimeout(function() {
          orbInteraction = value;
        }, delay);
      }

      //Function to continuously rotate the camera angle of the model viewer
      function updateOrbit() {
        //Call requestAnimationFrame inside of setTimeout to reduce the frequency at which the orb is updated
        const p = 80; //period at which the orb will update position (ms)
        const d = 0.008; //Δtheta that the orb will rotate every period (rad)

        setTimeout(() => {
          const orbit = mv.getCameraOrbit();
          if (!orbInteraction) {
            const phi = orbit.phi ? `${orbit.phi}rad` : "auto";
            mv.cameraOrbit = `${orbit.theta + d}rad ${phi} auto`;
            mv.jumpCameraToGoal();
          }
          requestAnimationFrame(updateOrbit);
        }, p);
      }
    },
  },
  computed: {
    publicPrivateText() {
      return this.resource && this.resource.public ? "Public" : "Private";
    },
    ...mapGetters(["currentUser"]),
    allPlacements() {
      return [...this.placements, ...this.placementsInFlight];
    },
    placementCards() {
      return this.placements.slice(0, Math.min(this.placements.length, 4));
    },
  },
  watch: {
    dropFiles: {
      deep: true,
      handler(val) {
        if (val.length && !this.uploading) this.createAndPlace();
      },
    },
    uploading(val) {
      if (!val && this.dropFiles.length) this.createAndPlace();
    },

    template(val) {
      if (!this.resource) return;
      this.resource.template = val.id;
    },
    resource: {
      deep: true,
      async handler(newVal, oldVal) {
        if (!oldVal) return; //Don't update when resource is first assigned
        await this.updateSpace();
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/bulma_overrides.scss";

$top-padding: 50px;
$iconWidth: 25pt;

.title-and-pill {
  display: flex;
  align-items: center;
}

.title {
  max-width: 100%;
  margin-bottom: 0px;
}

.pill {
  align-items: center;
  padding: 3px 5px;
  border: 1px solid var(--grey);
  border-radius: $radius;
  color: var(--grey);
  font-weight: 400;
  position: relative;
  top: -3px; // move it up 3px for more pleasing vertical centering
  font-size: 0.8rem;
  margin-left: 10px;
  cursor: pointer;
}
//Dont shift pill up quite so much if we're in animation-content (modal mode), bc introduces poor alignment with smaller title-font
.animation-content .pill {
  top: -1px;
}

.taglist {
  margin-top: 12px;
  margin-bottom: 30px;
  display: flex;
  flex-wrap: wrap;
  > [class^="tag"]:not(:last-child) {
    margin-right: 10px;
    margin-bottom: 10px;
  }
}

.center-align-icon {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 8px;
}

.tags {
  cursor: pointer;
}

.license-dropdown {
  display: block;
}

.inFlightOpacity {
  opacity: 50%;
  pointer-events: none;
  cursor: not-allowed;
}

// Staggered side panel transition
.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
}

.pano-wrapper {
  display: grid;

  .pano-child {
    grid-column-start: 1;
    grid-row-start: 1;
  }
  //Needed for fullscreen on Safari
  .pano-child:-webkit-full-screen {
    height: 100vh !important;
    width: 100vw !important;
  }

  .pano-overlay {
    background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.08),
      rgba(0, 0, 0, 0.3)
    );
    pointer-events: none;
    z-index: 10;
  }
}

//Orb constants
$orb-large: 175px;
$orb-large-shift: -87.5px;
$orb-small: 125px;
$orb-small-shift: -62.5px;
.body-container {
  border-top: 3px solid var(--grey-lighter);
  border-bottom: 3px solid var(--grey-lighter);
}

.control.tags {
  width: 100%;
}

.grid-container {
  display: grid;
  grid-template-columns: 3fr 300px;
  grid-column-gap: 80px;
  margin: auto;
  padding: $top-padding 0px;
  width: 90%;
  max-width: 1000px;

  .placements-table {
    .icon-parent {
      width: $iconWidth;
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      border-radius: $radius-large/2;
      overflow: hidden;
      justify-items: center;
      align-items: center;
      transform: translateZ(0);
      -webkit-transform: translateZ(0);

      > * {
        grid-area: 1/1;
      }

      .icon-background {
        height: calc(100% - 7%);
        width: calc(100% - 7%);
        border-radius: 5px;
        background-color: $primary;
      }
    }
  }

  p,
  #tags {
    margin-bottom: 1rem;
  }

  .orb-and-grid {
    margin-top: $top-padding * -1;

    // Grid for the cards in the right column
    .grid {
      display: grid;
      grid-template-columns: 1fr 0.3fr 1fr;
      margin-top: 10px;

      //.items classes; 0-n
      @for $i from 0 through 10 {
        @if $i % 2 == 0 {
          .item-#{$i} {
            grid-column-start: 1;
            grid-column-end: 3;
          }
        } @else {
          .item-#{$i} {
            grid-column-start: 2;
            grid-column-end: 4;
          }
        }
      }

      > div[class*="item-"] {
        margin-top: -20px; // creates overlap effect
      }
    }

    .orb-wrapper {
      width: 100%;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: -5%;
      user-select: none;

      .orb-and-shadow {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: 1fr;
        margin-top: $orb-large-shift; // move orb up to hover on centerline

        > * {
          position: relative;
          z-index: 10;
          margin-left: auto;
          grid-area: 1/1;
        }

        .orb {
          border-radius: 5000px;
          overflow: hidden;
          width: $orb-large;
          margin: auto;

          #modelViewer {
            width: 100%;
            height: $orb-large;
            transform: scale(-1, 1);
            --poster-color: "transparent";
          }
          > img {
            pointer-events: none;
            display: block;
            margin-left: auto;
            margin-right: 0;
          }
        }

        .shadow-wrapper {
          width: 100%;
          z-index: 9;
          pointer-events: none;
          .shadow {
            position: absolute;
            right: 65px;
            width: 50%;
            height: 40px;
            bottom: -11px;
            border-radius: 50%;
            z-index: -1;
            background: radial-gradient(
              ellipse at center,
              rgba(0, 0, 0, 1) 0%,
              rgba(0, 0, 0, 0) 100%
            );
            filter: blur(11px);
          }
        }
      }
    }
  }
}

.hero-image {
  height: 50vh;
  min-height: 200px;
}

// Media query to override default behavior and adjust View for mobile
@media screen and (max-width: ($tablet - 1)) {
  .grid-container {
    grid-template-columns: 3fr 200px;
    grid-column-gap: 30px;
    div[class*="item-"] {
      margin-top: -10px !important; // creates overlap effect
    }
    .icon-parent {
      height: 100%;
    }
    .orb-and-grid {
      .grid {
        margin-top: 0px;
      }
      .orb-wrapper {
        .orb-and-shadow {
          margin-top: $orb-small-shift;
          .orb {
            width: $orb-small;
            #modelViewer {
              height: $orb-small;
            }
          }
        }
      }
    }
  }
}

@media screen and (max-width: (500px)) {
  .grid-container {
    h3 {
      text-align: center;
    }
    grid-template-columns: 1fr;
    .grid,
    .more-text {
      display: none !important;
    }
    .orb-and-grid {
      order: 1;
      margin-bottom: -15px;
    }

    .placements-table-wrapper {
      order: 2;
    }
    .orb-wrapper {
      margin-bottom: $top-padding !important;
    }
    .orb-and-grid {
      .orb-wrapper {
        .orb-and-shadow {
          margin-top: $orb-large-shift;
          .orb {
            width: $orb-large;
            #modelViewer {
              height: $orb-large;
            }
          }
        }
      }
    }
  }
}
</style>
